import { useCallback, useMemo } from 'react';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import {
  ColumnFilterOption,
  GroupedData,
} from '@elementary/common-ui/components/Table/types';
import useGroupedPagedQuery from './useGroupedPagedQuery';
import useGroupExpansion from './useGroupExpansion';

type GroupDecorations = Pick<GroupedData<unknown>, 'icon' | 'metadataRenderer'>;

type Args<TItem, TResponse, TRow> = {
  groupBy: string | null;
  groupValues: ColumnFilterOption[];
  enabled: boolean;
  formatGroupTitle: (value: string) => string;
  buildQueryOptions: (value: string) => PagedQueryOptions<TItem, TResponse>;
  mapItems: (items: TItem[]) => TRow[];
  getGroupDecorations?: (value: string) => GroupDecorations;
};

const useGroupedTableData = <TItem, TResponse, TRow>({
  groupBy,
  groupValues,
  enabled,
  formatGroupTitle,
  buildQueryOptions,
  mapItems,
  getGroupDecorations,
}: Args<TItem, TResponse, TRow>) => {
  const titleToValue = useMemo(
    () =>
      new Map(
        groupValues.map((option) => [
          formatGroupTitle(option.value),
          option.value,
        ]),
      ),
    [groupValues, formatGroupTitle],
  );

  const groupTitles = useMemo(() => [...titleToValue.keys()], [titleToValue]);

  const { expandedGroups, expandedTitles, onGroupToggle } = useGroupExpansion({
    groupTitles,
    resetKey: groupBy,
  });

  const expandedGroupKeys = useMemo(
    () =>
      enabled
        ? expandedTitles.flatMap((title) => {
            const value = titleToValue.get(title);
            return value === undefined ? [] : [value];
          })
        : [],
    [enabled, expandedTitles, titleToValue],
  );

  const {
    groupFetchers,
    getRows,
    getGroupState,
    fetchNextGroupPage,
    isAnyFetching,
    isAnyFetchingWithRows,
    isInitialLoading,
  } = useGroupedPagedQuery<TItem, TResponse, TRow>({
    groupKeys: expandedGroupKeys,
    buildQueryOptions,
    mapItems,
  });

  const groupedData = useMemo<GroupedData<TRow>[]>(
    () =>
      groupValues.map((option) => {
        const title = formatGroupTitle(option.value);
        const groupState = getGroupState(option.value);
        return {
          title,
          count: option.count,
          ...getGroupDecorations?.(option.value),
          data: getRows(option.value),
          isLoading:
            !!expandedGroups[title] && (!groupState || groupState.isFetching),
        };
      }),
    [
      groupValues,
      formatGroupTitle,
      getGroupState,
      getGroupDecorations,
      getRows,
      expandedGroups,
    ],
  );

  const onGroupScrollToBottom = useCallback(
    (groupTitle: string) => fetchNextGroupPage(titleToValue.get(groupTitle)),
    [fetchNextGroupPage, titleToValue],
  );

  return {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetching,
    isAnyFetchingWithRows,
    isInitialLoading,
  };
};

export default useGroupedTableData;
